/**
 * `<McpCreateRoute>` — `/w/:slug/mcp/new`.
 *
 * Full in-shell create page for an MCP endpoint, matching the Final
 * `dashboard/final` NewEndpointView: a breadcrumb header (MCP › New endpoint)
 * over a centered form grouped into flat "Endpoint" + "Details" sections.
 * Replaces the old create dialog. On success it routes back to the list.
 */

import { useWorkspace } from "@cometchat-ai/auth/client";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Textarea,
  toast,
} from "@cometchat-ai/ui-dashboard";
import { zodResolver } from "@hookform/resolvers/zod";
import { Cable, ChevronRight, Image as ImageIcon, Info, Loader2 } from "../components/icons";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { z } from "zod";

import { useCreateSkill } from "../agents/api/skills";
import { Section } from "../agents/components/widget-config-parts";
import { PageShell } from "../components/page-shell";
import { formatApiError } from "../lib/format-error";

const addEndpointSchema = z.object({
  name: z.string().min(1).max(120),
  url: z.string().url(),
  description: z.string().min(1),
  iconUrl: z.union([z.string().url(), z.literal("")]),
});

type AddEndpointForm = z.infer<typeof addEndpointSchema>;

export function McpCreateRoute() {
  const { t } = useTranslation("workspace");
  const { workspace } = useWorkspace();
  const navigate = useNavigate();
  const create = useCreateSkill(workspace.slug);
  const backToList = () => navigate(`/w/${workspace.slug}/mcp`);

  const form = useForm<AddEndpointForm>({
    resolver: zodResolver(addEndpointSchema),
    defaultValues: { name: "", url: "", description: "", iconUrl: "" },
  });

  function onSubmit(values: AddEndpointForm) {
    create.mutate(
      {
        name: values.name,
        type: "mcp_server",
        description: values.description,
        inputSchema: {},
        configJsonb: values.iconUrl
          ? { url: values.url, iconUrl: values.iconUrl }
          : { url: values.url },
      },
      {
        onSuccess: () => {
          toast.success(t("mcp.dialog.addedToast"));
          backToList();
        },
        onError: (err) => {
          toast.error(t("mcp.dialog.addFailed"), { description: formatApiError(err as Error) });
        },
      },
    );
  }

  return (
    <PageShell
      data-testid="mcp-create-page"
      className="p-0"
      title={
        <div className="flex min-w-0 items-center gap-3">
          <button
            type="button"
            onClick={backToList}
            className="shrink-0 text-base font-semibold tracking-[0.01em] text-foreground transition-colors hover:text-muted-foreground"
          >
            {t("nav.mcp")}
          </button>
          <ChevronRight
            className="h-3.5 w-3.5 shrink-0 text-[color:var(--color-fg-subtle)]"
            aria-hidden="true"
          />
          <span className="truncate text-base font-medium tracking-[0.01em] text-muted-foreground">
            {t("mcp.create.title")}
          </span>
        </div>
      }
      actions={
        <>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={backToList}
            disabled={create.isPending}
            className="h-8 rounded-md text-[13px]"
            data-testid="mcp-create-cancel"
          >
            {t("mcp.dialog.cancel")}
          </Button>
          <Button
            type="submit"
            form="mcp-create-form"
            size="sm"
            disabled={create.isPending}
            className="h-8 gap-1.5 rounded-md text-[13px] hover:bg-primary-hover"
            data-testid="mcp-create-submit"
          >
            {create.isPending && (
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            )}
            {create.isPending ? t("mcp.dialog.submitting") : t("mcp.dialog.submit")}
          </Button>
        </>
      }
    >
      <div className="px-6 py-6">
        <div className="mx-auto w-full max-w-6xl">
          <Form {...form}>
            <form
              id="mcp-create-form"
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-3"
              noValidate
            >
              <Section title={t("mcp.dialog.endpointSection")} icon={Cable} defaultOpen>
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-[13px] font-medium text-foreground">
                          {t("mcp.dialog.name.label")}
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder={t("mcp.dialog.name.placeholder")}
                            data-testid="mcp-name"
                            className="h-10 rounded-md text-[13px] placeholder:text-[color:var(--color-fg-subtle)]"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="url"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-[13px] font-medium text-foreground">
                          {t("mcp.dialog.url.label")}
                        </FormLabel>
                        <FormControl>
                          <Input
                            type="url"
                            placeholder={t("mcp.dialog.url.placeholder")}
                            data-testid="mcp-url"
                            className="h-10 rounded-md text-[13px] placeholder:text-[color:var(--color-fg-subtle)]"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </Section>

              <Section title={t("mcp.dialog.detailsSection")} icon={Info} defaultOpen>
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-[13px] font-medium text-foreground">
                          {t("mcp.dialog.descField.label")}
                        </FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder={t("mcp.dialog.descField.placeholder")}
                            rows={3}
                            data-testid="mcp-description"
                            className="min-h-0 resize-none rounded-md text-[13px] placeholder:text-[color:var(--color-fg-subtle)]"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="iconUrl"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-[13px] font-medium text-foreground">
                          {t("mcp.dialog.iconUrl.label")}
                        </FormLabel>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-10 w-10 shrink-0 rounded-md border border-border">
                            {field.value ? (
                              <AvatarImage src={field.value} alt="" className="object-cover" />
                            ) : null}
                            <AvatarFallback className="rounded-md bg-muted text-muted-foreground">
                              <ImageIcon className="h-[18px] w-[18px]" aria-hidden="true" />
                            </AvatarFallback>
                          </Avatar>
                          <FormControl>
                            <Input
                              type="url"
                              data-testid="mcp-icon-url"
                              placeholder={t("mcp.dialog.iconUrl.placeholder")}
                              className="h-10 flex-1 rounded-md text-[13px] placeholder:text-[color:var(--color-fg-subtle)]"
                              {...field}
                            />
                          </FormControl>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {t("mcp.dialog.iconUrl.hint")}
                        </p>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </Section>
            </form>
          </Form>
        </div>
      </div>
    </PageShell>
  );
}
